import { useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Save, Trash2, Loader2, ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { useBranding, BrandMark } from "@/lib/branding";

const inp = "w-full bg-muted/40 border border-border rounded-lg px-4 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50";

export default function BrandingSettings() {
  const b = useBranding();
  const fileRef = useRef(null);
  const [name, setName] = useState(b.brand_name);
  const [tagline, setTagline] = useState(b.brand_tagline);
  const [logo, setLogo] = useState(b.logo_data_url);
  const [testimonialsUrl, setTestimonialsUrl] = useState(b.testimonials_url || "");
  const [saving, setSaving] = useState(false);

  const pick = () => fileRef.current?.click();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Escolha uma imagem"); return; }
    if (f.size > 2_000_000) { toast.error("Imagem demasiado grande (máx 2MB)"); return; }
    const r = new FileReader();
    r.onload = () => setLogo(r.result);
    r.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/branding", { id: "singleton", brand_name: name, brand_tagline: tagline, logo_data_url: logo, testimonials_url: testimonialsUrl.trim() || null });
      toast.success("Marca atualizada");
      b.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); } finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm("Remover o logo atual?")) return;
    await api.delete("/branding/logo");
    setLogo(null);
    toast.success("Logo removido");
    b.reload();
  };

  return (
    <div className="p-8 lg:p-12 max-w-3xl mx-auto space-y-6" data-testid="branding-root">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Administração</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Marca</h1>
        <p className="text-muted-foreground text-sm mt-2">Personalize o logo e o nome que aparecem em toda a aplicação e nas propostas partilhadas com clientes.</p>
      </div>

      <div className="bg-card border border-border rounded-2xl p-8 space-y-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-3">Pré-visualização</div>
          <div className="bg-muted/40 border border-border rounded-xl p-6 flex items-center gap-6">
            <div className="flex items-center gap-3">
              {logo ? (
                <img src={logo} alt="Logo" className="w-14 h-14 rounded-lg object-contain bg-muted" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#3B82F6] to-[#059669] flex items-center justify-center text-foreground font-display text-2xl">{(name||'C')[0]}</div>
              )}
              <div>
                <div className="font-display text-2xl">{name || "—"}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">{tagline}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Nome da marca</div>
            <input data-testid="brand-name" className={inp} value={name} onChange={(e)=>setName(e.target.value)} />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Tagline</div>
            <input data-testid="brand-tagline" className={inp} value={tagline} onChange={(e)=>setTagline(e.target.value)} />
          </label>
          <label className="block md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">
              URL da página de testemunhos <span className="normal-case tracking-normal text-muted-foreground/70">(mostrada na proposta como link de credibilidade)</span>
            </div>
            <input
              data-testid="brand-testimonials-url"
              type="url"
              className={inp}
              placeholder="https://caring.pt/testemunhos"
              value={testimonialsUrl}
              onChange={(e)=>setTestimonialsUrl(e.target.value)}
            />
          </label>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-3">Logo</div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="brand-file-input" />
          <div className="flex flex-wrap gap-3">
            <button type="button" data-testid="brand-pick-file" onClick={pick} className="inline-flex items-center gap-2 bg-muted hover:bg-muted border border-border rounded-full px-4 py-2 text-sm">
              <Upload className="w-4 h-4" /> Carregar imagem
            </button>
            {logo && (
              <button type="button" data-testid="brand-clear" onClick={clear} className="inline-flex items-center gap-2 border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 rounded-full px-4 py-2 text-sm">
                <Trash2 className="w-4 h-4" /> Remover
              </button>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> PNG, JPG ou SVG · máx. 2MB · idealmente quadrado</div>
        </div>

        <div className="flex justify-end">
          <button data-testid="brand-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 text-background font-semibold rounded-full px-6 py-3 text-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar alterações
          </button>
        </div>
      </div>
    </div>
  );
}
